import React, { useRef, useState, useMemo } from "react";

import {
  Button,
  Card,
  Divider,
  Group,
  Stack,
  UnstyledButton,
  Text,
  useMantineTheme,
  Badge,
  Box,
  ScrollArea,
  Loader,
  LoadingOverlay,
  Autocomplete,
} from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import _ from "lodash";

import { descriptions } from "constants/levelData";
import { JobTitleOptionValue } from "lib/frontend/benchmarking/types";
import { levelLabel } from "utils/utils";

type IOnSelectArgs = {
  family: string;
  level: string;
};

interface FamilyLevelAssistedPickerProps {
  onSelect: (args: IOnSelectArgs) => void;
  close: () => void;
}

type ResponseFormat = Array<{
  job_family: string;
  normalized_titles: string[];
  levels: {
    level: string;
    normalized_titles: string[];
  }[];
}>;

export function FamilyLevelAssistedPicker({
  onSelect,
  close,
}: FamilyLevelAssistedPickerProps): JSX.Element {
  const [family, setFamily] = useState("");
  const [level, setLevel] = useState("");

  const [response, setResponse] = useState<ResponseFormat>([]);
  const [fetching, setFetching] = useState(false);

  const [jobTitlesPage, setJobTitlesPage] = useState(0);
  const [jobTitles, setJobTitles] = useState<JobTitleOptionValue[]>([]);

  const search = useRef(
    _.debounce(function (query: string) {
      if (!query) {
        return;
      }

      setFetching(true);
      return fetch(`/api/benchmarking/families/suggest-by-title/${query.trim().toLowerCase()}`)
        .then((res) => res.json())
        .then((data) => {
          // filter out families "other"
          const refinedData = _.filter(data, (d) => d.job_family?.toLowerCase() !== "other");
          setResponse(refinedData);
          setFetching(false);
        });
    }, 500),
  );

  function addToJobTitles(newTitles: JobTitleOptionValue[]) {
    setJobTitles((oldTitles) => {
      const resultingTitles = _([...oldTitles, ...newTitles])
        // order before uniq to keep the new counts from the server
        .orderBy("count", "desc")
        .uniqBy("normalizedTitle")
        .valueOf();

      return resultingTitles;
    });
  }

  async function fetchJobTitles(page = 0, query = "") {
    return fetch(`/api/benchmarking/titles-autocomplete/${query}?page=${page}`)
      .then((res) => res.json())
      .then((data) => {
        const formattedJobTitles = _.map(data, (jt) => ({
          normalizedTitle: jt.normalizedTitle,
          jobFunction: jt.jobFunction,
          count: jt.count,
        }));

        addToJobTitles(formattedJobTitles);
      });
  }

  const familyOptions = useMemo(() => {
    return _.map(response, (f) => ({
      label: f.job_family,
      value: f.job_family,
      titles: f.normalized_titles,
    }));
  }, [response]);

  const levelOptions = useMemo(() => {
    const familyObj = _.find(response, (f) => f.job_family === family);
    if (!familyObj) {
      return [];
    }

    return _(familyObj.levels)
      .map((l) => {
        const levelDescription = descriptions[l.level as keyof typeof descriptions];

        if (_.isNil(levelDescription)) {
          return undefined;
        }

        return {
          label: levelLabel(l.level),
          value: l.level,
          titles: l.normalized_titles,
        };
      })
      .compact()
      .valueOf();
  }, [family, response]);

  return (
    <Stack gap={16}>
      <Autocomplete
        leftSection={<IconSearch size={16} />}
        onChange={(e) => {
          search.current(e);
          setJobTitles([]);
          fetchJobTitles(jobTitlesPage, e);
        }}
        comboboxProps={{
          zIndex: 1000,
        }}
        placeholder="Start by typing a job title..."
        rightSection={fetching ? <Loader size="xs" /> : null}
        data={jobTitles.map((jt) => jt.normalizedTitle)}
      />
      <Group grow align="stretch">
        <PickerCard
          title="Next, select a job family..."
          noOptionsText="Job families will populate after job title"
          options={familyOptions}
          onSelect={(family) => {
            setFamily(family);
            setLevel("");
          }}
          selected={family}
          fetching={fetching}
        />
        <PickerCard
          title={family ? `Then, choose a level for ${family}` : "Then, choose a level..."}
          noOptionsText={"Levels will populate after job family"}
          options={levelOptions}
          onSelect={setLevel}
          selected={level}
          fetching={fetching}
        />
      </Group>
      <Divider />
      <Group wrap="nowrap" justify="flex-end">
        <Button size="sm" variant="tertiary" onClick={close}>
          Cancel
        </Button>
        <Button
          size="sm"
          data-heap-id="benchmarking-search-button"
          disabled={!family || !level}
          onClick={() =>
            onSelect({
              family,
              level,
            })
          }
          variant="primary"
        >
          Search
        </Button>
      </Group>
    </Stack>
  );
}

interface PickerCardProps {
  title: string;
  options: { label: string; value: string; titles: string[] }[];
  selected?: string;
  onSelect: (option: string) => void;
  numTitlesSuggested?: number;
  noOptionsText?: string;
  fetching?: boolean;
}

function PickerCard({
  title,
  options,
  onSelect,
  selected,
  numTitlesSuggested = 3,
  noOptionsText,
  fetching = false,
}: PickerCardProps): JSX.Element {
  const theme = useMantineTheme();

  function renderOptions() {
    return (
      <ScrollArea h="100%" type="hover">
        <Stack gap={0}>
          {options.map((o, index) => {
            const topSuggested = index === 0;
            const isSelected = o.value === selected;

            return (
              <UnstyledButton
                px={8}
                py={8}
                key={o.value}
                sx={{
                  background: isSelected ? theme.colors.brand[1] : "transparent",
                }}
                onClick={() => onSelect(o.value)}
              >
                <Stack>
                  <Group justify="space-between">
                    <Text fw="bold">{o.label}</Text>
                    <Text fz={12} c={topSuggested ? "green" : "blue"}>
                      {topSuggested ? "Top Suggested" : "Suggested"}
                    </Text>
                  </Group>
                  <Group>
                    {_.map(o.titles.slice(0, numTitlesSuggested), (t) => (
                      <Badge
                        sx={{ textTransform: "none", fontWeight: 300 }}
                        key={t}
                        color="gray"
                        variant="light"
                        bg={theme.colors.gray[2]}
                      >
                        {t}
                      </Badge>
                    ))}
                  </Group>
                </Stack>
              </UnstyledButton>
            );
          })}
        </Stack>
      </ScrollArea>
    );
  }

  function renderNoOptions() {
    return (
      <Box p={8}>
        <Text size="sm" c="gray.6" py={2}>
          {noOptionsText}
        </Text>
      </Box>
    );
  }

  return (
    <Card shadow="none">
      <Card.Section withBorder px={8}>
        <LoadingOverlay visible={fetching} />
        <Text size="sm" c="gray.6" py={2}>
          {title}
        </Text>
      </Card.Section>
      <Card.Section h={400} py={4}>
        {options.length ? renderOptions() : renderNoOptions()}
      </Card.Section>
    </Card>
  );
}
